import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, Stack, Group, Title, Text, Button } from '@repletoryreach/sdk-ui';
import { StatusBadge, Alert } from '@repletoryreach/sdk-ui/data-display';
import { admin } from '@repletoryreach/sdk-api';
import { useT } from '../../i18n/I18nProvider';

/**
 * Service health on the dashboard, from the probe endpoint.
 *
 * This card used to render eight hardcoded services with invented uptimes and
 * a permanently degraded Webhooks row, sitting beside real KPIs with nothing
 * but a small "demo" hint to separate them. `GET /v1/admin/ops/health` has
 * existed since Phase 8 and the system health page already reads it, so the
 * numbers here are now the same probe results, and a failed probe says so
 * instead of inventing 99.9%.
 */
export function HealthGrid() {
  const t = useT();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'ops', 'health'],
    queryFn: () => admin.listServiceHealth(),
    refetchInterval: 30_000
  });

  const rows = data?.data ?? [];

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="baseline">
          <Title order={4}>{t('system.health.title')}</Title>
          <Text fz="xs" c="var(--rr-color-text-muted)">{t('healthGrid.subtitle')}</Text>
        </Group>

        {error ? (
          <Alert color="error" title={t('healthGrid.loadError')}>
            <Button size="xs" variant="outline" onClick={() => refetch()} loading={isFetching}>
              {t('action.retry')}
            </Button>
          </Alert>
        ) : isLoading ? (
          <Text fz="sm" c="var(--rr-color-text-muted)">{t('common.loading')}</Text>
        ) : rows.length === 0 ? (
          <Text fz="sm" c="var(--rr-color-text-muted)">{t('healthGrid.empty')}</Text>
        ) : (
          <div className="rr-admin-health-grid">
            {rows.map((svc) => (
              <Card key={svc.service} withBorder>
                <Stack gap="xs" style={{ minWidth: 0 }}>
                  <Text fz="sm" fw={600} lineClamp={1}>{svc.service}</Text>
                  <StatusBadge
                    status={svc.status === 'healthy' ? 'healthy' : svc.status === 'down' ? 'down' : 'warning'}
                    size="xs"
                  />
                  <Text fz="xs" c="var(--rr-color-text-muted)">
                    {t('healthGrid.probe', { status: svc.http_status || '–', ms: svc.duration_ms })}
                  </Text>
                </Stack>
              </Card>
            ))}
          </div>
        )}

        <Link to="/system/health" style={{ color: 'var(--rr-color-primary)', fontSize: 13 }}>
          {t('healthGrid.viewAll')}
        </Link>
      </Stack>
    </Card>
  );
}
